import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import html2canvas from 'html2canvas';

/**
 * Encapsulates all map capture logic to keep the main map component smaller.
 */
@Injectable({
    providedIn: 'root',
})
export class LeafletCaptureService {
    /** Selectors hidden temporarily during capture to avoid UI artifacts. */
    private readonly selectorsToHide: string[] = [
        '.leaflet-control-container',
        '.leaflet-tooltip',
        '.leaflet-popup',
        '.map-layers-fab',
        '.opacity-fab',
        '.opacity-container',
        '.location-fab',
        '.location-container',
        '.location-list',
        '.center-fab',
        '.globe-fab',
        '.windy-fab',
        '.custom-layers-panel-wrapper',
    ];

    /**
     * Waits for the next animation frame.
     *
     * @returns Promise resolved on the next frame.
     */
    private nextFrame(): Promise<void> {
        return new Promise((resolve) => requestAnimationFrame(() => resolve()));
    }

    /**
     * Generic sleep helper.
     *
     * @param ms Milliseconds.
     * @returns Promise resolved after the delay.
     */
    private sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Returns whether any TileLayer-like layer still appears to be loading.
     *
     * @param map Leaflet map.
     * @returns True if pending tile loads are detected.
     */
    private hasPendingTileLoads(map: L.Map): boolean {
        let pending = false;

        map.eachLayer((layer: any) => {
        const tilesToLoad = typeof layer?._tilesToLoad === 'number' ? layer._tilesToLoad : 0;
        const isLoading = !!layer?._loading;
        if (isLoading || tilesToLoad > 0) {
            pending = true;
        }
        });

        return pending;
    }

    /**
     * Waits until the map looks stable enough for capture.
     *
     * @param map Leaflet map.
     * @param options Fine tuning options.
     */
    async waitForMapReadyForCapture(
        map: L.Map,
        options?: {
        timeoutMs?: number;
        settleFrames?: number;
        minWaitMs?: number;
        pollMs?: number;
        }
    ): Promise<void> {
        const timeoutMs = options?.timeoutMs ?? 12000;
        const settleFrames = options?.settleFrames ?? 2;
        const minWaitMs = options?.minWaitMs ?? 150;
        const pollMs = options?.pollMs ?? 100;

        if (!map) {
        return;
        }

        try {
        map.invalidateSize(true);
        } catch {
        /** No-op: invalidation may fail during transient lifecycle moments. */
        }

        const start = Date.now();
        await this.sleep(minWaitMs);

        await new Promise<void>((resolve) => {
        let done = false;

        const finish = () => {
            if (done) {
            return;
            }
            done = true;
            map.off('moveend', finish);
            map.off('zoomend', finish);
            resolve();
        };

        const anyMap: any = map as any;
        const isAnimatingZoom = !!anyMap?._animatingZoom;

        if (!isAnimatingZoom) {
            setTimeout(finish, 0);
            return;
        }

        map.on('moveend', finish);
        map.on('zoomend', finish);
        setTimeout(finish, 500);
        });

        while (this.hasPendingTileLoads(map) && Date.now() - start < timeoutMs) {
        await this.sleep(pollMs);
        }

        for (let index = 0; index < settleFrames; index++) {
        await this.nextFrame();
        }

        try {
        map.invalidateSize(true);
        } catch {
        /** No-op. */
        }

        await this.nextFrame();
    }

    /**
     * Captures the visible map element as a PNG data URL.
     *
     * @param map Leaflet map.
     * @param mapContainer Host element containing the Leaflet container.
     * @returns PNG data URL in base64 form.
     */
    async captureMapAsBase64(map: L.Map, mapContainer: HTMLElement): Promise<string> {
        await this.waitForMapReadyForCapture(map);

        const target = mapContainer?.querySelector('.leaflet-container') || mapContainer;
        if (!target) {
        throw new Error('Map container not found');
        }

        const hiddenElements: Array<{ el: HTMLElement; display: string; visibility: string }> = [];

        this.selectorsToHide.forEach((selector) => {
        const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
        elements.forEach((element) => {
            hiddenElements.push({
            el: element,
            display: element.style.display,
            visibility: element.style.visibility,
            });
            element.style.visibility = 'hidden';
        });
        });

        try {
        const canvas = await html2canvas(target as HTMLElement, {
            allowTaint: false,
            useCORS: true,
            scale: 1,
            backgroundColor: '#ffffff',
            logging: false,
        });

        return canvas.toDataURL('image/png');
        } finally {
        hiddenElements.forEach(({ el, display, visibility }) => {
            el.style.display = display;
            el.style.visibility = visibility;
        });
        }
    }
}